import { useCallback, useEffect, useRef, useState } from "react";
import type { ReplayFile, RuntimeSnapshot } from "@bastion/game-core";
import { parseReplayFile, parseRuntimeSnapshot, serializeReplayFile } from "@bastion/game-core";
import { useI18n } from "./i18n";

export interface ManagementPanelProps {
  onClose: () => void;
  canImportProgress: boolean;
  onExportProgress: () => RuntimeSnapshot;
  onImportProgress: (snapshot: RuntimeSnapshot) => void;
  onExportReplay: () => ReplayFile;
  onImportReplay: (replay: ReplayFile) => void;
  replayMode: boolean;
}

async function compressReplay(source: string): Promise<Blob> {
  if (typeof CompressionStream === "undefined") {
    throw new Error("当前浏览器不支持 gzip 压缩");
  }
  const stream = new Blob([source], { type: "application/json" })
    .stream()
    .pipeThrough(new CompressionStream("gzip"));
  const compressed = await new Response(stream).arrayBuffer();
  return new Blob([compressed], { type: "application/gzip" });
}

async function readReplayFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!isGzip) return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  if (typeof DecompressionStream === "undefined") {
    throw new Error("当前浏览器不支持 gzip 解压");
  }
  const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Response(stream).text();
}

export function ManagementPanel({
  onClose,
  canImportProgress,
  onExportProgress,
  onImportProgress,
  onExportReplay,
  onImportReplay,
  replayMode,
}: ManagementPanelProps) {
  const [progressMessage, setProgressMessage] = useState("");
  const [replayMessage, setReplayMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const replayInputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
    return () => previousFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleExportProgress = useCallback(() => {
    try {
      const snapshot = onExportProgress();
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `lastbastion-${snapshot.mapId}-progress.json`;
      anchor.click();
      URL.revokeObjectURL(url);
      setProgressMessage(t("settings_progress_exported"));
    } catch (error) {
      setProgressMessage(
        t("settings_progress_import_error", {
          message: error instanceof Error ? error.message : String(error),
        }),
      );
    }
  }, [onExportProgress, t]);

  const handleImportProgress = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;
      try {
        if (!canImportProgress) throw new Error(t("settings_progress_import_locked"));
        const snapshot = parseRuntimeSnapshot(await file.text());
        onImportProgress(snapshot);
        setProgressMessage(t("settings_progress_imported"));
      } catch (error) {
        setProgressMessage(
          t("settings_progress_import_error", {
            message: error instanceof Error ? error.message : String(error),
          }),
        );
      }
    },
    [canImportProgress, onImportProgress, t],
  );

  const handleExportReplay = useCallback(async () => {
    try {
      const replay = onExportReplay();
      const blob = await compressReplay(serializeReplayFile(replay));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `lastbastion-${replay.mapId}-replay.json.gz`;
      anchor.click();
      URL.revokeObjectURL(url);
      setReplayMessage(t("settings_replay_exported"));
    } catch (error) {
      setReplayMessage(
        t("settings_replay_error", {
          message: error instanceof Error ? error.message : String(error),
        }),
      );
    }
  }, [onExportReplay, t]);

  const handleImportReplay = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      input.value = "";
      if (!file) return;
      try {
        const replay = parseReplayFile(await readReplayFile(file));
        onImportReplay(replay);
      } catch (error) {
        setReplayMessage(
          t("settings_replay_error", {
            message: error instanceof Error ? error.message : String(error),
          }),
        );
      }
    },
    [onImportReplay, t],
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <div
      className="settings-overlay management-overlay"
      data-testid="management-overlay"
      onClick={handleBackdropClick}
    >
      <div
        className="settings-panel management-panel"
        data-testid="management-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="management-title"
      >
        <div className="settings-header">
          <h2 className="settings-title" id="management-title">
            {t("management_title")}
          </h2>
          <div className="settings-header-actions">
            <button
              ref={closeButtonRef}
              className="settings-close"
              data-testid="management-close-btn"
              onClick={onClose}
              aria-label={t("close")}
            >
              ✕
            </button>
          </div>
        </div>

        {/* 对局进度 */}
        <section className="settings-section" data-testid="settings-progress-section">
          <h3 className="settings-section-title">{t("settings_progress")}</h3>
          <div className="settings-reference-actions">
            <button
              className="settings-sound-btn enabled"
              data-testid="export-progress-btn"
              onClick={handleExportProgress}
            >
              {t("settings_export_progress")}
            </button>
            <button
              className="settings-sound-btn enabled"
              data-testid="import-progress-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canImportProgress}
            >
              {t("settings_import_progress")}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              data-testid="progress-file-input"
              onChange={handleImportProgress}
            />
          </div>
          <p className="settings-hint">{t("settings_import_progress_hint")}</p>
          {!canImportProgress && (
            <p className="settings-hint" data-testid="progress-import-locked">
              {t("settings_progress_import_locked")}
            </p>
          )}
          {progressMessage && (
            <p className="settings-saved-hint" data-testid="progress-message" role="status">
              {progressMessage}
            </p>
          )}
        </section>

        {/* 战斗回放 */}
        <section className="settings-section" data-testid="settings-replay-section">
          <h3 className="settings-section-title">{t("settings_replay")}</h3>
          <div className="settings-reference-actions">
            <button
              className="settings-sound-btn enabled"
              data-testid="export-replay-btn"
              onClick={handleExportReplay}
            >
              {t("settings_replay_export")}
            </button>
            <button
              className="settings-sound-btn enabled"
              data-testid="import-replay-btn"
              disabled={replayMode}
              onClick={() => replayInputRef.current?.click()}
            >
              {t("settings_replay_import")}
            </button>
            <input
              ref={replayInputRef}
              type="file"
              accept="application/json,application/gzip,application/x-gzip,.json,.gz"
              hidden
              data-testid="replay-file-input"
              onChange={handleImportReplay}
            />
          </div>
          <p className="settings-hint">{t("settings_replay_hint")}</p>
          {replayMessage && (
            <p className="settings-saved-hint" data-testid="replay-message" role="status">
              {replayMessage}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
